// src/components/ModalBox/ModalBox.tsx
//
// A generic, application-window-style dialog.
//
// It knows about windows and dialogs. It knows nothing about the content it
// carries: there is no form logic, no validation, no Query terminology, and no
// assumption about what "children" is. The same component is expected to serve
// forms, skill lists, project write-ups and contact details.
//
// Behaviour it owns, so no caller has to re-implement it:
//   - rendering into a portal, so no ancestor's overflow, transform or
//     stacking context can clip or trap it
//   - Escape, backdrop click, and a Tab loop that cannot escape the window
//   - focus moved in on open and returned to the opener on close
//   - background scroll lock, released on close and on unmount
//   - open/close motion, including a reduced-motion path
//
// It deliberately does NOT implement minimise or maximise. The yellow and green
// dots are decorative; the red one closes. A minimise button that does nothing
// is a lie to anyone using it, so there isn't one.
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, useReducedMotion, type Variants } from 'framer-motion';

import { DURATION, EASE_OUT } from '../about/motion';
import {
  Body,
  CloseLight,
  DecorativeLight,
  Footer,
  Root,
  Scrim,
  TitleBar,
  TrafficLights,
  Window,
  WindowTitle,
  type ModalBoxSize,
} from './ModalBox.styles';

/**
 * Everything that can hold focus inside the window. `[tabindex]:not([tabindex="-1"])`
 * is last so that native controls win the ordering, and `:not([disabled])` keeps
 * the submit button out of the loop while a send is in flight.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalBoxProps {
  /** Controlled by the caller. The modal never opens or closes itself. */
  isOpen: boolean;
  /**
   * Called for every dismissal path: the red dot, Escape, and the backdrop.
   * The caller decides what that means — for a form it may also need to reset
   * state, so it is passed straight through rather than swallowed here.
   */
  onClose: () => void;
  /** Shown in the title bar and used as the dialog's accessible name. */
  title: string;
  /** Arbitrary content. Scrolls inside the window if it overflows. */
  children?: ReactNode;
  /** Optional pinned strip below the body, e.g. a submit row. */
  footer?: ReactNode;
  /** Window width preset. `md` suits forms, `lg` suits long-form content. */
  size?: ModalBoxSize;
  className?: string;
}

/** Dimmed backdrop: a plain fade. */
const scrimVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.panelIn, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: DURATION.panelOut, ease: EASE_OUT } },
};

/**
 * The window: a small scale-up and a short rise. Deliberately restrained — a
 * desktop window should settle, not spring, and the site has no bouncing motion
 * anywhere (see about/motion).
 */
const windowVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 12 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: DURATION.panelIn, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    y: 6,
    transition: { duration: DURATION.panelOut, ease: EASE_OUT },
  },
};

/** Reduced motion: fade in place. No travel, no scale. */
const scrimVariantsReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.panelInReduced } },
  exit: { opacity: 0, transition: { duration: DURATION.panelOutReduced } },
};

const windowVariantsReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.panelInReduced } },
  exit: { opacity: 0, transition: { duration: DURATION.panelOutReduced } },
};

const ModalBox = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className,
}: ModalBoxProps) => {
  const windowRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion() ?? false;

  // Escape and the Tab loop are bound to the document rather than the window,
  // so they keep working even in the frame before focus has moved inside.
  const titleId = useId();

  /**
   * The parent's onClose is read through a ref so the keydown listener can be
   * registered once per open/close. An inline arrow from the caller would
   * otherwise re-run the effect on every parent render, adding and removing the
   * document listener each time.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const scrimMotion = reduceMotion ? scrimVariantsReduced : scrimVariants;
  const windowMotion = reduceMotion ? windowVariantsReduced : windowVariants;

  /* ------------------------------------------- open: focus in, lock scroll */

  useEffect(() => {
    if (!isOpen) return undefined;

    // Captured before anything moves focus, so this is the element that opened
    // the dialog.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // `overflow: hidden` rather than the position:fixed technique, because
    // fixed would drop the page to the top on mobile. The inline value is
    // captured and put back verbatim so this composes with any other lock.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Deferred a frame so the content is laid out and the first focusable is
    // in the document. Focusing a node that is about to be animated in is
    // unreliable otherwise.
    const frame = requestAnimationFrame(() => {
      const node = windowRef.current;
      if (!node) return;

      // An element can nominate itself as the landing point with
      // data-modal-autofocus — a form's first field, for instance. Otherwise
      // the first focusable in the window, which for a plain dialog is the
      // close control.
      const preferred = node.querySelector<HTMLElement>('[data-modal-autofocus]');
      const target = preferred ?? node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? node;
      target.focus({ preventScroll: true });
    });

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;

      // `document.contains` guards the case where the opener has since been
      // unmounted, e.g. the trigger lived in a list that re-rendered.
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === 'function' &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  /* --------------------------------------------------- Escape and Tab loop */

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const node = windowRef.current;
      if (!node) return;

      // getClientRects() filters out anything not rendered, so a hidden or
      // display:none control cannot become a focus stop.
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.getClientRects().length > 0
      );

      if (focusable.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // The `!node.contains(active)` arm also covers focus having ended up
      // outside the window, which would otherwise let Tab walk the page behind
      // the modal.
      if (event.shiftKey) {
        if (active === first || !node.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !node.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  /* ------------------------------------------------- backdrop dismissal */

  /**
   * A press that starts inside the window and is released on the backdrop must
   * not dismiss — otherwise selecting text in the content and letting go over
   * the dim area closes the dialog and loses the selection. Recording where the
   * press began is what distinguishes the two.
   */
  const pressStartedOnBackdrop = useRef(false);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pressStartedOnBackdrop.current = event.target === event.currentTarget;
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pressStartedOnBackdrop.current && event.target === event.currentTarget) {
      onCloseRef.current();
    }
    pressStartedOnBackdrop.current = false;
  };

  const handleClose = useCallback(() => onCloseRef.current(), []);

  /* --------------------------------------------------------------- render */

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <Root
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          className={className}
        >
          {/* pointer-events: none, so the press registers on Root. */}
          <Scrim
            aria-hidden="true"
            variants={scrimMotion}
            initial="hidden"
            animate="visible"
            exit="exit"
          />

          <Window
            ref={windowRef}
            $size={size}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            /* Not a tab stop; it is the fallback target if the window somehow
               contains no focusable element. */
            tabIndex={-1}
            variants={windowMotion}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <TitleBar>
              <TrafficLights>
                <CloseLight type="button" onClick={handleClose} aria-label={`Close ${title}`} />
                <DecorativeLight $color="yellow" aria-hidden="true" />
                <DecorativeLight $color="green" aria-hidden="true" />
              </TrafficLights>
              <WindowTitle id={titleId}>{title}</WindowTitle>
            </TitleBar>

            <Body>{children}</Body>

            {footer ? <Footer>{footer}</Footer> : null}
          </Window>
        </Root>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ModalBox;
